import { useI18n } from 'vue-i18n';
import { useTheme } from '@lumi/ui';
import { readDemoPersona } from '@lumi/app-kit';
import { useAuthStore } from '@/stores/auth';
import type { Tenant } from '@/stores/tenants';
import { appendSessionHandoff } from '@/utils/module-links';
import {
  runtimeApprenticeUrl,
  runtimeBackofficeUrl,
  runtimeLumiDomain,
  runtimeLumiProtocol,
  runtimePeopleUrl,
  runtimeTimeTrackingUrl,
} from '@/utils/runtime-config';

// The one way this app launches a peer module: Suite Home's tiles and panels and
// the setup page's entries all resolve the host and hand the session over here,
// so the two can never land in different places.

/** What it takes to address a module: its env URL, local dev URL and path segment. */
export type ModuleLaunchTarget = {
  envUrl: string;
  defaultLocalUrl: string;
  prodSubdomain: string;
};

// Keyed by the tenant's module key (the user-api long key).
export const MODULE_LAUNCH_TARGETS: Readonly<Record<string, ModuleLaunchTarget>> = {
  ats: { envUrl: runtimeBackofficeUrl(), defaultLocalUrl: 'http://localhost:5173', prodSubdomain: 'backoffice' },
  time_tracking: { envUrl: runtimeTimeTrackingUrl(), defaultLocalUrl: 'http://localhost:5176', prodSubdomain: 'time' },
  apprenticeship: { envUrl: runtimeApprenticeUrl(), defaultLocalUrl: 'http://localhost:5178', prodSubdomain: 'apprentice' },
  people: { envUrl: runtimePeopleUrl(), defaultLocalUrl: 'http://localhost:5181', prodSubdomain: 'people' },
};

function looksMalformedModuleUrl(value: string) {
  const raw = String(value || '').toLowerCase();
  if (!raw) return true;
  if (raw.includes('{') || raw.includes('}') || raw.includes('%7b') || raw.includes('%7d')) return true;
  try {
    new URL(value);
    return false;
  } catch {
    return true;
  }
}

function lumiDomainFromWorkspaceHost() {
  if (typeof window === 'undefined') return '';
  const host = String(window.location.hostname || '').toLowerCase();
  if (!host || host === 'localhost' || host.endsWith('.localhost')) return '';
  const labels = host.split('.');
  if (labels.length < 3) return '';
  return labels.slice(1).join('.');
}

export function buildModuleUrl(tenant: Pick<Tenant, 'slug'>, mod: ModuleLaunchTarget): string {
  if (mod.envUrl) {
    const candidate = mod.envUrl.includes('{tenant}') ? mod.envUrl.split('{tenant}').join(tenant.slug) : mod.envUrl;
    if (!looksMalformedModuleUrl(candidate)) return candidate;
  }
  const lumiDomain = runtimeLumiDomain();
  const hostDerivedDomain = lumiDomainFromWorkspaceHost();
  const resolvedDomain = lumiDomain && !lumiDomain.includes('{') && !lumiDomain.includes('}') ? lumiDomain : hostDerivedDomain;
  if (resolvedDomain) return `${runtimeLumiProtocol()}://${tenant.slug}.${resolvedDomain}/${mod.prodSubdomain}`;
  return mod.defaultLocalUrl;
}

/**
 * Full-page navigate to a module with the live token, tenant, locale, theme and
 * demo persona, so the target authenticates and lands deep-linked.
 */
export function useModuleLaunch() {
  const authStore = useAuthStore();
  const { locale } = useI18n();
  const { theme } = useTheme();

  function ssoNavigate(baseUrl: string, tenant: Tenant, deepLink?: string, forceTokens = false) {
    const withPath = deepLink
      ? baseUrl.replace(/\/$/, '') + (deepLink.startsWith('/') ? deepLink : `/${deepLink}`)
      : baseUrl;
    const url = new URL(withPath);
    appendSessionHandoff(
      url,
      { token: authStore.token, refreshToken: authStore.refreshToken },
      {
        tenantSlug: tenant.slug,
        tenantName: tenant.name,
        locale: locale.value,
        theme: theme.value,
        // The mask survives the switch (#409): the target module's router stores
        // the same choice (a persona, or the deliberate 'none') before its first
        // request, so no view there is ever fetched as the wrong identity.
        demoPersona: readDemoPersona(tenant.slug, authStore.user?.userId),
      },
      { forceTokens },
    );

    window.location.href = url.toString();
  }

  function openModule(tenant: Tenant, mod: ModuleLaunchTarget, deepLink?: string) {
    ssoNavigate(buildModuleUrl(tenant, mod), tenant, deepLink);
  }

  return { openModule };
}
